import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Sparkles, ShieldCheck, HeartPulse, Users, GraduationCap, ScanLine, Coins,
  Download, ArrowRight, ArrowUpRight, Globe, Cpu, Lock, Network, TrendingUp,
  Zap, Layers, Rocket, Mail, CheckCircle2, Gauge, Share2,
  BookOpen, ListChecks, Leaf, BarChart3, Film, MapPin, Car, Home, Calendar,
  Utensils, Newspaper, Flower2, Video, Bed, Wallet,
} from "lucide-react";

/* ------------------------------------------------------------------ *
 *  My Kind — Investor Pitch Deck / Premium Brochure
 *  Standalone, self-contained dark-cosmic styling (theme-independent).
 *  Route: /investors (alias /pitch). Print-to-PDF ready.
 * ------------------------------------------------------------------ */

const AMBER = "#FF9D00";

const SLIDES = [
  { id: "cover", label: "Vision" },
  { id: "problem", label: "Problem" },
  { id: "solution", label: "Product" },
  { id: "platform", label: "Platform" },
  { id: "why-now", label: "Why Now" },
  { id: "market", label: "Market" },
  { id: "model", label: "Model" },
  { id: "moat", label: "Moat" },
  { id: "traction", label: "Traction" },
  { id: "team", label: "Team" },
  { id: "ask", label: "The Ask" },
  { id: "closing", label: "Contact" },
];

const Overline = ({ children }) => (
  <span className="text-[11px] uppercase tracking-[0.25em] font-mono font-semibold" style={{ color: AMBER }}>{children}</span>
);

const Section = ({ id, children, className = "" }) => (
  <section id={id} data-testid={`slide-${id}-section`}
    className={`deck-slide relative min-h-screen flex flex-col justify-center px-5 sm:px-10 lg:px-20 py-20 ${className}`}>
    <div className="max-w-7xl mx-auto w-full">{children}</div>
  </section>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}>{children}</motion.div>
);

const Glass = ({ children, className = "", testId }) => (
  <div data-testid={testId}
    className={`rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl shadow-2xl ${className}`}>{children}</div>
);

/* ----------------------------- Interactive: TAM capture ---------------------------- */
function MarketSim() {
  const [capture, setCapture] = useState(1.2); // % of SOM captured
  const SOM = 24; // $B serviceable obtainable (10yr)
  const revenue = ((SOM * 1000) * (capture / 100)); // in $M
  return (
    <Glass className="p-6 sm:p-8" testId="tam-sim">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <Overline>Capture model</Overline>
          <p className="text-sec text-sm mt-2 max-w-sm" style={{ color: "#A1A1AA" }}>
            Drag to model My Kind's share of the $24B serviceable obtainable market.
          </p>
        </div>
        <div className="text-right">
          <div className="font-mono font-bold text-3xl sm:text-4xl text-white tabular-nums">
            ${revenue >= 1000 ? `${(revenue / 1000).toFixed(2)}B` : `${revenue.toFixed(0)}M`}
          </div>
          <div className="text-[11px] uppercase tracking-widest" style={{ color: "#71717A" }}>modeled annual revenue</div>
        </div>
      </div>
      <div className="mt-6">
        <input data-testid="tam-calculator-slider" type="range" min="0.2" max="8" step="0.1" value={capture}
          onChange={(e) => setCapture(parseFloat(e.target.value))}
          className="w-full deck-range" />
        <div className="flex justify-between mt-2 text-[11px] font-mono" style={{ color: "#71717A" }}>
          <span>0.2%</span>
          <span className="text-white">{capture.toFixed(1)}% of SOM</span>
          <span>8%</span>
        </div>
      </div>
    </Glass>
  );
}

/* ----------------------------- Interactive: Revenue engine ---------------------------- */
function RevenueSim() {
  const [users, setUsers] = useState(5); // millions MAU
  const arpu = 9; // $/yr blended (credits + ventures + creator take)
  const take = 0.72; // net after creator payouts
  const rev = users * 1_000_000 * arpu * take; // $/yr
  const fmt = (n) => n >= 1e9 ? `$${(n / 1e9).toFixed(2)}B` : `$${(n / 1e6).toFixed(0)}M`;
  return (
    <Glass className="p-6 sm:p-8" testId="revenue-sim">
      <Overline>Revenue engine</Overline>
      <div className="grid sm:grid-cols-2 gap-6 mt-5 items-end">
        <div>
          <label className="text-sm" style={{ color: "#A1A1AA" }}>Monthly active members</label>
          <div className="font-mono font-bold text-2xl text-white mt-1">{users}M</div>
          <input data-testid="revenue-users-slider" type="range" min="0.5" max="50" step="0.5" value={users}
            onChange={(e) => setUsers(parseFloat(e.target.value))} className="w-full deck-range mt-3" />
        </div>
        <div className="rounded-xl p-5 border border-white/10" style={{ background: "rgba(255,157,0,0.08)" }}>
          <div className="text-[11px] uppercase tracking-widest" style={{ color: "#71717A" }}>Net annual revenue</div>
          <div className="font-mono font-bold text-3xl sm:text-4xl mt-1" style={{ color: AMBER }}>{fmt(rev)}</div>
          <div className="text-[11px] mt-2" style={{ color: "#71717A" }}>${arpu} blended ARPU · {Math.round(take * 100)}% net after creator payouts</div>
        </div>
      </div>
    </Glass>
  );
}

export default function Investors() {
  const [active, setActive] = useState("cover");

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }),
      { threshold: 0.5 }
    );
    document.querySelectorAll(".deck-slide").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const promises = [
    { icon: ShieldCheck, t: "Sovereignty", d: "You own your data, identity and creations — never sold behind your back." },
    { icon: Globe, t: "Equality", d: "Region-fair pricing: everyone pays what's fair for where they live." },
    { icon: GraduationCap, t: "Knowledge", d: "Ask anything, learn anything — a living encyclopedia the community grows." },
    { icon: Users, t: "Fun", d: "Communities, travel, meals and events — live and play across borders." },
    { icon: Coins, t: "Earn", d: "Turn work, referrals, knowledge and kindness into real earnings." },
  ];

  const problems = [
    { icon: Layers, stat: "15+", label: "disconnected apps", d: "Families juggle a dozen single-purpose apps — social, health, learning, payments — none that talk to each other." },
    { icon: Lock, stat: "$450B", label: "data sold / yr", d: "Surveillance ad models extract and monetise personal data. Users create the value; platforms keep it." },
    { icon: HeartPulse, stat: "64%", label: "feel isolated", d: "Attention-extractive feeds grow loneliness instead of real belonging — especially across generations." },
  ];

  const products = [
    { icon: Users, t: "Kind Social", d: "A kinder, ad-free family & community feed — posts, photos, comments, replies and circles you control." },
    { icon: HeartPulse, t: "Digital Twin + Guardian SOS", d: "Live vitals, a wellness index and a 15-second SOS that shares precise location in emergencies." },
    { icon: Sparkles, t: "ANXYA — AI for everything", d: "A personal assistant that drafts, interprets documents, plans and builds — in your language." },
    { icon: GraduationCap, t: "VishwaGuru University", d: "10,000+ courses with exams, certificates and a learn-to-earn economy for the whole community." },
    { icon: ScanLine, t: "PurityPower Scanner", d: "Scan any label or receipt — flags harmful ingredients, grades purchases, suggests cleaner swaps." },
    { icon: Lock, t: "Data Vault", d: "Own your data & IP, earn from anonymised insights, and host your apps — sovereignty by design." },
  ];

  const whyNow = [
    { icon: Cpu, t: "Generative AI, everywhere", d: "AI is finally good and cheap enough to personalise every surface — not bolted on, built in." },
    { icon: ShieldCheck, t: "Data-sovereignty era", d: "DPDP, GDPR and user backlash make ownership a feature people now demand, not a nice-to-have." },
    { icon: Network, t: "The super-app shift", d: "The West is ready for what Asia proved: one trusted app beats fifteen extractive ones." },
  ];

  const moat = [
    { icon: Zap, t: "Self-evolving platform", d: "6th-gen software that improves itself and adapts to each region, widening the gap over time." },
    { icon: Lock, t: "Data sovereignty", d: "Ownership + earnings create trust incumbents can't copy without cannibalising ad revenue." },
    { icon: Network, t: "Family-graph effects", d: "Multi-generational circles + 3-tier referrals compound retention far beyond single-user social." },
    { icon: Coins, t: "Creator economy flywheel", d: "Every save, mint and scan becomes reusable, earning assets — supply that compounds demand." },
  ];

  // ILLUSTRATIVE placeholders — the founder replaces these with live numbers.
  const traction = [
    { n: "10,000+", l: "courses live", s: "across every subject" },
    { n: "4 ways", l: "members earn", s: "save · mint · refer · bounties" },
    { n: "9/6/3%", l: "referral tiers", s: "three generations deep" },
    { n: "PWA", l: "installs in seconds", s: "no app store needed" },
  ];

  const markets = [
    { l: "Social", v: 220, c: AMBER },
    { l: "Digital Health", v: 350, c: "#007AFF" },
    { l: "EdTech", v: 180, c: "#10B981" },
    { l: "Search & AI", v: 120, c: "#A78BFA" },
    { l: "Creator Fintech", v: 90, c: "#F472B6" },
  ];

  const allocation = [
    { l: "Core AI & Engineering", p: 40, c: AMBER },
    { l: "Growth & Network", p: 30, c: "#007AFF" },
    { l: "Data Security Infra", p: 20, c: "#10B981" },
    { l: "Operations", p: 10, c: "#A1A1AA" },
  ];

  return (
    <div data-testid="pitch-deck-container" className="deck-root text-white" style={{ background: "#05050A" }}>
      {/* Self-contained cosmic styling + print rules */}
      <style>{`
        .deck-root { font-family: 'Manrope','Plus Jakarta Sans',ui-sans-serif,system-ui,sans-serif; scroll-behavior:smooth; }
        .deck-root .font-serif { font-family: 'Fraunces','Cormorant Garamond',Georgia,serif; }
        .deck-root .font-mono { font-family: 'JetBrains Mono',ui-monospace,monospace; }
        .deck-grid-bg {
          background-image:
            radial-gradient(60rem 60rem at 80% -10%, rgba(255,157,0,0.12), transparent 60%),
            radial-gradient(50rem 50rem at -10% 110%, rgba(0,122,255,0.10), transparent 60%),
            radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px);
          background-size: auto, auto, 44px 44px;
        }
        .deck-range { -webkit-appearance:none; appearance:none; height:4px; border-radius:9999px; background:rgba(255,255,255,0.14); outline:none; }
        .deck-range::-webkit-slider-thumb { -webkit-appearance:none; width:20px; height:20px; border-radius:9999px; background:${AMBER}; cursor:pointer; box-shadow:0 0 0 6px rgba(255,157,0,0.18); }
        .deck-range::-moz-range-thumb { width:20px; height:20px; border:none; border-radius:9999px; background:${AMBER}; cursor:pointer; box-shadow:0 0 0 6px rgba(255,157,0,0.18); }
        @media print {
          @page { size: landscape; margin: 0; }
          .deck-no-print { display:none !important; }
          .deck-slide { min-height:auto !important; page-break-after:always; padding:36px 48px !important; }
          .deck-root { -webkit-print-color-adjust:exact; print-color-adjust:exact; }
        }
      `}</style>

      {/* Sticky top nav */}
      <header className="deck-no-print fixed top-0 inset-x-0 z-50 backdrop-blur-xl border-b border-white/10" style={{ background: "rgba(5,5,10,0.7)" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-10 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 font-serif text-lg tracking-tight">
            <span className="w-7 h-7 rounded-lg grid place-items-center" style={{ background: "rgba(255,157,0,0.15)", color: AMBER }}><Sparkles size={15} /></span>
            My Kind <span className="hidden sm:inline text-[11px] font-mono uppercase tracking-[0.2em] ml-1" style={{ color: "#71717A" }}>· Investor Brief</span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="nav-download-pdf-button" onClick={() => window.print()}
              className="h-9 px-4 rounded-full text-sm font-medium flex items-center gap-1.5 transition-colors hover:brightness-110"
              style={{ background: AMBER, color: "#050505" }}>
              <Download size={15} /> <span className="hidden sm:inline">Download PDF</span>
            </button>
            <Link to="/" data-testid="nav-open-app" className="h-9 px-4 rounded-full text-sm flex items-center gap-1.5 border border-white/15 text-white/90 hover:bg-white/10 hover:text-white transition-colors">
              Open app <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </header>

      {/* Progress bar */}
      <div className="deck-no-print fixed top-14 left-0 z-40 h-0.5" style={{ background: AMBER, width: `${((SLIDES.findIndex((s) => s.id === active) + 1) / SLIDES.length) * 100}%`, transition: "width .4s ease" }} />

      {/* Right slide dots */}
      <nav className="deck-no-print fixed right-4 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col gap-3">
        {SLIDES.map((s) => (
          <button key={s.id} data-testid={`slide-nav-dot-${s.id}`} onClick={() => jump(s.id)} title={s.label}
            className="group flex items-center gap-2 justify-end">
            <span className="text-[10px] font-mono uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "#A1A1AA" }}>{s.label}</span>
            <span className="w-2.5 h-2.5 rounded-full transition-all" style={{ background: active === s.id ? AMBER : "rgba(255,255,255,0.25)", transform: active === s.id ? "scale(1.3)" : "scale(1)" }} />
          </button>
        ))}
      </nav>

      {/* 1 — COVER */}
      <Section id="cover" className="deck-grid-bg pt-20">
        <div className="grid lg:grid-cols-[1.25fr_1fr] gap-12 items-center">
          <div>
            <Reveal><Overline>My Kind · Vasudhaiva Kutumbakam — the world is one family</Overline></Reveal>
            <Reveal delay={0.05}>
              <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.04] mt-5">
                One app for <span style={{ color: AMBER }}>everything</span>,<br />for <span style={{ color: AMBER }}>everyone</span>.
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-lg sm:text-xl mt-6 max-w-xl leading-relaxed" style={{ color: "#A1A1AA" }}>
                A self-evolving, <b className="text-white">data-sovereign super-app</b> that unifies connection, wellbeing,
                learning and earning — giving every person the power of AI to do everything, while they own their data and earn from it.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="flex flex-wrap items-center gap-3 mt-8">
                <button data-testid="launch-tour-button" onClick={() => jump("problem")}
                  className="h-12 px-6 rounded-full text-sm font-medium flex items-center gap-2 hover:brightness-110 transition-[filter]" style={{ background: AMBER, color: "#050505" }}>
                  <Rocket size={17} /> Launch the pitch
                </button>
                <a href="https://mykind.app" target="_blank" rel="noopener noreferrer"
                  className="h-12 px-5 rounded-full text-sm flex items-center gap-2 border border-white/15 hover:bg-white/10 transition-colors">
                  mykind.app <ArrowUpRight size={15} />
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="flex flex-wrap gap-2 mt-8">
                {["AI-Native", "Self-Evolving", "Data-Sovereign", "Region-Fair"].map((p) => (
                  <span key={p} className="text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-full border border-white/10" style={{ color: "#A1A1AA", background: "rgba(255,255,255,0.03)" }}>{p}</span>
                ))}
              </div>
            </Reveal>
          </div>

          {/* App mockup */}
          <Reveal delay={0.1}>
            <div className="relative mx-auto w-[260px] sm:w-[300px]">
              <div className="absolute -inset-10 rounded-full blur-3xl" style={{ background: "rgba(255,157,0,0.18)" }} />
              <div className="relative rounded-[2.5rem] border border-white/15 p-3 shadow-2xl" style={{ background: "#0A0A0F" }}>
                <div className="rounded-[2rem] overflow-hidden border border-white/10" style={{ background: "#05050A" }}>
                  <div className="px-4 py-3 flex items-center justify-between border-b border-white/10">
                    <span className="font-serif">My Kind</span>
                    <span className="w-6 h-6 rounded-full" style={{ background: "rgba(255,157,0,0.2)" }} />
                  </div>
                  <div className="p-3 space-y-3">
                    {[{ i: Users, t: "Kind Social" }, { i: HeartPulse, t: "Live vitals · 72 bpm" }, { i: Sparkles, t: "Ask ANXYA" }, { i: Coins, t: "You earned +3 credits" }].map((r, k) => (
                      <div key={k} className="flex items-center gap-3 rounded-xl p-3 border border-white/10" style={{ background: "rgba(255,255,255,0.03)" }}>
                        <span className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: "rgba(255,157,0,0.15)", color: AMBER }}><r.i size={16} /></span>
                        <span className="text-sm" style={{ color: "#D4D4D8" }}>{r.t}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      {/* 2 — PROBLEM */}
      <Section id="problem" className="deck-grid-bg">
        <Reveal><Overline>The problem</Overline></Reveal>
        <Reveal delay={0.05}><h2 className="font-serif text-3xl sm:text-5xl tracking-tight mt-4 max-w-3xl">The digital world is fragmented, extractive and lonely.</h2></Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {problems.map((p, i) => (
            <Reveal key={p.label} delay={i * 0.08}>
              <Glass className="p-7 h-full" testId={`problem-card-${i}`}>
                <p.icon size={22} style={{ color: AMBER }} />
                <div className="font-mono font-bold text-4xl mt-5 text-white">{p.stat}</div>
                <div className="text-sm uppercase tracking-widest mt-1" style={{ color: "#71717A" }}>{p.label}</div>
                <p className="text-sm mt-4 leading-relaxed" style={{ color: "#A1A1AA" }}>{p.d}</p>
              </Glass>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 3 — SOLUTION */}
      <Section id="solution" className="deck-grid-bg">
        <Reveal><Overline>The solution</Overline></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-serif text-3xl sm:text-5xl tracking-tight mt-4 max-w-3xl">
            One trusted app — <span style={{ color: AMBER }}>connect, be well, learn & earn</span>.
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {products.map((p, i) => (
            <Reveal key={p.t} delay={(i % 3) * 0.07}>
              <Glass className="p-6 h-full hover:-translate-y-1.5 transition-transform duration-300" testId={`solution-card-${i}`}>
                <span className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: "rgba(255,157,0,0.15)", color: AMBER }}><p.icon size={21} /></span>
                <h3 className="font-serif text-xl mt-5">{p.t}</h3>
                <p className="text-sm mt-2 leading-relaxed" style={{ color: "#A1A1AA" }}>{p.d}</p>
              </Glass>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 4 — WHY NOW */}
      <Section id="why-now" className="deck-grid-bg">
        <Reveal><Overline>Why now</Overline></Reveal>
        <Reveal delay={0.05}><h2 className="font-serif text-3xl sm:text-5xl tracking-tight mt-4 max-w-3xl">A once-in-a-decade tri-convergence.</h2></Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {whyNow.map((w, i) => (
            <Reveal key={w.t} delay={i * 0.08}>
              <Glass className="p-7 h-full" testId={`why-now-card-${i}`}>
                <div className="font-mono text-sm" style={{ color: "#71717A" }}>0{i + 1}</div>
                <w.icon size={24} className="mt-4" style={{ color: AMBER }} />
                <h3 className="font-serif text-xl mt-4">{w.t}</h3>
                <p className="text-sm mt-2 leading-relaxed" style={{ color: "#A1A1AA" }}>{w.d}</p>
              </Glass>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 5 — MARKET */}
      <Section id="market" className="deck-grid-bg">
        <Reveal><Overline>Market size</Overline></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-serif text-3xl sm:text-5xl tracking-tight mt-4">
            A <span style={{ color: AMBER }}>$960B+</span> convergence TAM.
          </h2>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-10 mt-12 items-center">
          <div className="space-y-3">
            {markets.map((m, i) => (
              <Reveal key={m.l} delay={i * 0.06}>
                <div data-testid={`market-bar-${i}`} className="flex items-center gap-4">
                  <div className="w-36 text-sm shrink-0" style={{ color: "#D4D4D8" }}>{m.l}</div>
                  <div className="flex-1 h-8 rounded-lg overflow-hidden border border-white/10" style={{ background: "rgba(255,255,255,0.03)" }}>
                    <motion.div initial={{ width: 0 }} whileInView={{ width: `${(m.v / 350) * 100}%` }} viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: i * 0.06 }} className="h-full rounded-lg" style={{ background: m.c }} />
                  </div>
                  <div className="w-16 text-right font-mono text-sm tabular-nums text-white">${m.v}B</div>
                </div>
              </Reveal>
            ))}
            <p className="text-xs mt-4" style={{ color: "#71717A" }}>TAM across the categories My Kind unifies. SAM (digital-first, AI-ready markets) ≈ $190B; 10-yr SOM ≈ $24B.</p>
          </div>
          <Reveal delay={0.1}><MarketSim /></Reveal>
        </div>
      </Section>

      {/* 6 — BUSINESS MODEL */}
      <Section id="model" className="deck-grid-bg">
        <Reveal><Overline>Business model</Overline></Reveal>
        <Reveal delay={0.05}><h2 className="font-serif text-3xl sm:text-5xl tracking-tight mt-4 max-w-3xl">A credits micro-economy where members earn too.</h2></Reveal>
        <div className="grid md:grid-cols-4 gap-5 mt-12">
          {[
            { n: "Kind Credits", d: "Pay-per-use AI across search, health, learning & creation — region-fair pricing." },
            { n: "15% creator take", d: "Members earn every time their saved reports & minted work are reused." },
            { n: "9 / 6 / 3%", d: "Three-generation referral rewards power viral, trusted family growth." },
            { n: "B2B & Ventures", d: "Enterprise health, education and venture integrations on the same rails." },
          ].map((b, i) => (
            <Reveal key={b.n} delay={i * 0.07}>
              <Glass className="p-6 h-full" testId={`model-card-${i}`}>
                <div className="font-serif text-2xl" style={{ color: AMBER }}>{b.n}</div>
                <p className="text-sm mt-3 leading-relaxed" style={{ color: "#A1A1AA" }}>{b.d}</p>
              </Glass>
            </Reveal>
          ))}
        </div>
        <div className="mt-8"><Reveal delay={0.1}><RevenueSim /></Reveal></div>
      </Section>

      {/* 7 — MOAT */}
      <Section id="moat" className="deck-grid-bg">
        <Reveal><Overline>Moat &amp; defensibility</Overline></Reveal>
        <Reveal delay={0.05}><h2 className="font-serif text-3xl sm:text-5xl tracking-tight mt-4 max-w-3xl">Compounding advantages incumbents can't copy.</h2></Reveal>
        <div className="grid sm:grid-cols-2 gap-5 mt-12">
          {moat.map((m, i) => (
            <Reveal key={m.t} delay={(i % 2) * 0.08}>
              <Glass className="p-7 h-full flex gap-4" testId={`moat-card-${i}`}>
                <span className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: "rgba(255,157,0,0.15)", color: AMBER }}><m.icon size={20} /></span>
                <div>
                  <h3 className="font-serif text-xl">{m.t}</h3>
                  <p className="text-sm mt-2 leading-relaxed" style={{ color: "#A1A1AA" }}>{m.d}</p>
                </div>
              </Glass>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* 8 — TRACTION */}
      <Section id="traction" className="deck-grid-bg">
        <Reveal><Overline>Traction &amp; product depth</Overline></Reveal>
        <Reveal delay={0.05}><h2 className="font-serif text-3xl sm:text-5xl tracking-tight mt-4 max-w-3xl">Live product, real earning rails, shipping fast.</h2></Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
          {traction.map((t, i) => (
            <Reveal key={t.l} delay={i * 0.07}>
              <Glass className="p-6 text-center h-full" testId={`traction-metric-${i}`}>
                <div className="font-mono font-bold text-3xl sm:text-4xl" style={{ color: AMBER }}>{t.n}</div>
                <div className="text-sm mt-2 text-white">{t.l}</div>
                <div className="text-[11px] mt-1" style={{ color: "#71717A" }}>{t.s}</div>
              </Glass>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}>
          <p className="text-xs mt-6 font-mono" style={{ color: "#71717A" }}>
            ↳ Illustrative — replace with your live growth metrics (MAU, retention, revenue, SOS nodes) before investor meetings.
          </p>
        </Reveal>
      </Section>

      {/* 9 — TEAM */}
      <Section id="team" className="deck-grid-bg">
        <Reveal><Overline>Team</Overline></Reveal>
        <Reveal delay={0.05}><h2 className="font-serif text-3xl sm:text-5xl tracking-tight mt-4 max-w-3xl">Builders obsessed with kindness and compounding systems.</h2></Reveal>
        <div className="grid sm:grid-cols-3 gap-5 mt-12">
          {["Founder & CEO", "Co-founder & CTO", "Head of Growth"].map((role, i) => (
            <Reveal key={role} delay={i * 0.08}>
              <Glass className="p-6 h-full" testId={`team-card-${i}`}>
                <div className="w-16 h-16 rounded-2xl" style={{ background: "linear-gradient(135deg, rgba(255,157,0,0.3), rgba(0,122,255,0.25))" }} />
                <div className="font-serif text-xl mt-4">Your name</div>
                <div className="text-sm" style={{ color: AMBER }}>{role}</div>
                <p className="text-sm mt-3 leading-relaxed" style={{ color: "#A1A1AA" }}>Add a one-line track record — the lab, unicorn or company that makes investors lean in.</p>
              </Glass>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.1}><p className="text-xs mt-6 font-mono" style={{ color: "#71717A" }}>↳ Replace with real founders, advisors and standout credentials.</p></Reveal>
      </Section>

      {/* 10 — THE ASK */}
      <Section id="ask" className="deck-grid-bg">
        <div className="grid lg:grid-cols-[1fr_1fr] gap-12 items-center">
          <div>
            <Reveal><Overline>The ask</Overline></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-serif text-4xl sm:text-6xl tracking-tight mt-4">
                Raising <span style={{ color: AMBER }}>$3M</span> seed
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-lg mt-5 max-w-md leading-relaxed" style={{ color: "#A1A1AA" }}>
                To scale the AI core, acquire the first million families, and harden data-sovereign infrastructure —
                18 months of runway to category leadership.
              </p>
            </Reveal>
            <Reveal delay={0.15}><p className="text-xs mt-5 font-mono" style={{ color: "#71717A" }}>↳ Edit the amount, stage, valuation and runway to your real terms.</p></Reveal>
          </div>
          <Reveal delay={0.1}>
            <Glass className="p-7" testId="allocation-card">
              <div className="text-sm uppercase tracking-widest" style={{ color: "#71717A" }}>Use of funds</div>
              <div className="space-y-4 mt-5">
                {allocation.map((a, i) => (
                  <div key={a.l} data-testid={`allocation-${i}`}>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span style={{ color: "#D4D4D8" }}>{a.l}</span>
                      <span className="font-mono text-white">{a.p}%</span>
                    </div>
                    <div className="h-2.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
                      <motion.div initial={{ width: 0 }} whileInView={{ width: `${a.p}%` }} viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: i * 0.08 }} className="h-full rounded-full" style={{ background: a.c }} />
                    </div>
                  </div>
                ))}
              </div>
            </Glass>
          </Reveal>
        </div>
      </Section>

      {/* 11 — CLOSING */}
      <Section id="closing" className="deck-grid-bg text-center">
        <Reveal>
          <span className="inline-flex w-16 h-16 rounded-2xl items-center justify-center mx-auto" style={{ background: "rgba(255,157,0,0.15)", color: AMBER }}><Sparkles size={28} /></span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-serif text-4xl sm:text-6xl tracking-tight mt-6 max-w-3xl mx-auto leading-[1.05]">
            Let's build the world's <span style={{ color: AMBER }}>kindest</span> super-app.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-lg mt-6 max-w-xl mx-auto" style={{ color: "#A1A1AA" }}>
            One app for everything, for everyone — owned by the people who use it. Vasudhaiva Kutumbakam.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-9">
            <a data-testid="contact-schedule-meeting-button" href="mailto:founders@mykind.app?subject=My%20Kind%20—%20Investor%20conversation"
              className="h-12 px-6 rounded-full text-sm font-medium flex items-center gap-2 hover:brightness-110 transition-[filter]" style={{ background: AMBER, color: "#050505" }}>
              <Mail size={17} /> founders@mykind.app
            </a>
            <button data-testid="closing-download-pdf" onClick={() => window.print()}
              className="h-12 px-5 rounded-full text-sm flex items-center gap-2 border border-white/15 hover:bg-white/10 transition-colors">
              <Download size={16} /> Download this deck
            </button>
            <a href="https://mykind.app" target="_blank" rel="noopener noreferrer"
              className="h-12 px-5 rounded-full text-sm flex items-center gap-2 border border-white/15 hover:bg-white/10 transition-colors">
              mykind.app <ArrowUpRight size={15} />
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="flex items-center justify-center gap-5 mt-10 text-[11px] font-mono uppercase tracking-widest" style={{ color: "#52525B" }}>
            <span className="flex items-center gap-1.5"><CheckCircle2 size={13} style={{ color: "#10B981" }} /> Live product</span>
            <span className="flex items-center gap-1.5"><Gauge size={13} style={{ color: AMBER }} /> AI-native</span>
            <span className="flex items-center gap-1.5"><ShieldCheck size={13} style={{ color: "#007AFF" }} /> Data-sovereign</span>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
